import { Button, Container, Group, Stack, Text, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { usePermissions } from '@/contexts/PermissionContext';
import { useDisclosure } from '@mantine/hooks';
import { useMedplum, useSearchResources } from '@medplum/react-hooks';
import type { Organization } from '@medplum/fhirtypes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ORG_KIND_BILLING_ENTITY, ORG_KIND_SYSTEM } from '@unify/fhir-model/constants';
import {
  buildBillingEntityOrganization,
  ensureBillingEntityType,
  getOrganizationEin,
  getOrganizationNpi,
  getOrganizationPhone,
  getOrganizationUspsAddress,
  getStripeConnectedAccountId,
  setOrganizationEin,
  setOrganizationNpi,
  setOrganizationPhone,
  setOrganizationUspsAddress,
  setStripeConnectedAccountId,
} from '@unify/fhir-model/resources/organization';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import type { BillingEntityFormInput } from '@/schemas/billingEntity.schema';
import { AddBillingEntityModal } from './AddBillingEntityModal';

type InvalidateSearches = (resourceType: 'Organization') => void;

interface BillingEntityRow {
  id: string;
  name: string;
  state?: string;
  npi?: string;
  ein?: string;
  stripeAccount?: string;
  organization: Organization;
}

export function prepareBillingEntityDirectoryList(
  medplum: { invalidateSearches: InvalidateSearches },
  setListReady: (ready: boolean) => void,
): void {
  medplum.invalidateSearches('Organization');
  setListReady(true);
}

export function refreshBillingEntityDirectoryList(
  medplum: { invalidateSearches: InvalidateSearches },
  bumpListKey: () => void,
): void {
  medplum.invalidateSearches('Organization');
  bumpListKey();
}

export function updateBillingEntityOrganization(
  organization: Organization,
  values: BillingEntityFormInput,
): Organization {
  let updated: Organization = ensureBillingEntityType({
    ...organization,
    name: values.name.trim(),
    active: true,
  });
  updated = setOrganizationNpi(updated, values.npi);
  updated = setOrganizationEin(updated, values.ein);
  updated = setOrganizationPhone(updated, values.phone);
  updated = setOrganizationUspsAddress(updated, {
    line: values.street,
    city: values.city,
    state: values.state,
    postalCode: values.postalCode,
  });
  return setStripeConnectedAccountId(updated, values.stripeConnectedAccountId);
}

export function truncateStripeAccountId(value: string | undefined): string {
  if (!value) return '';
  return value.length > 12 ? `${value.slice(0, 12)}…` : value;
}

interface BillingEntityDirectoryTableProps {
  canWrite: boolean;
  onRowClick: (row: BillingEntityRow) => void;
}

function BillingEntityDirectoryTable({
  canWrite,
  onRowClick,
}: BillingEntityDirectoryTableProps): JSX.Element {
  const [orgs] = useSearchResources('Organization', {
    type: `${ORG_KIND_SYSTEM}|${ORG_KIND_BILLING_ENTITY}`,
    _count: '500',
    _sort: 'name',
  });

  const rows = useMemo(
    () =>
      (orgs ?? [])
        .filter((org) => org.id && org.name)
        .map((org) => {
          const address = getOrganizationUspsAddress(org);
          return {
            id: org.id as string,
            name: org.name as string,
            state: address.state,
            npi: getOrganizationNpi(org),
            ein: getOrganizationEin(org),
            stripeAccount: getStripeConnectedAccountId(org),
            organization: org,
          };
        }),
    [orgs],
  );

  const columns = useMemo((): UnifyTableColumn<BillingEntityRow>[] => {
    return [
      { key: 'name', label: 'Name', width: 280 },
      { key: 'state', label: 'State', getValue: (r) => r.state ?? '', width: 88 },
      { key: 'npi', label: 'NPI', getValue: (r) => r.npi ?? '' },
      { key: 'ein', label: 'EIN', getValue: (r) => r.ein ?? '' },
      {
        key: 'stripeAccount',
        label: 'Stripe account',
        getValue: (r) => truncateStripeAccountId(r.stripeAccount),
      },
    ];
  }, []);

  return (
    <UnifyTable
      columns={columns}
      data={rows}
      getRowKey={(r) => r.id}
      onRowClick={canWrite ? onRowClick : undefined}
      searchable
      searchPlaceholder="Search billing entities…"
      sortable
      pagination
      defaultRowsPerPage={25}
      scrollMinWidth={720}
    />
  );
}

export function BillingEntitiesSettings(): JSX.Element {
  const medplum = useMedplum();
  const { role } = usePermissions();
  const canWrite = role === 'unify-admin';
  const [modalOpened, { open, close }] = useDisclosure(false);
  const [selected, setSelected] = useState<BillingEntityRow>();
  const [modalKey, setModalKey] = useState(0);
  const [listReady, setListReady] = useState(false);
  const [listKey, setListKey] = useState(0);

  useEffect(() => {
    prepareBillingEntityDirectoryList(medplum, setListReady);
  }, [medplum]);

  const openAddModal = useCallback(() => {
    setSelected(undefined);
    setModalKey((key) => key + 1);
    open();
  }, [open]);

  const openEditModal = useCallback(
    (row: BillingEntityRow) => {
      if (!canWrite) return;
      setSelected(row);
      setModalKey((key) => key + 1);
      open();
    },
    [canWrite, open],
  );

  const handleSave = useCallback(
    async (values: BillingEntityFormInput) => {
      if (selected) {
        await medplum.updateResource(updateBillingEntityOrganization(selected.organization, values));
      } else {
        await medplum.createResource(
          buildBillingEntityOrganization({
            name: values.name,
            npi: values.npi,
            ein: values.ein,
            phone: values.phone,
            street: values.street,
            city: values.city,
            state: values.state,
            postalCode: values.postalCode,
            stripeConnectedAccountId: values.stripeConnectedAccountId,
          }),
        );
      }
      refreshBillingEntityDirectoryList(medplum, () => setListKey((key) => key + 1));
    },
    [medplum, selected],
  );

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <div>
            <Title order={2} mb="xs">
              Billing entities
            </Title>
            <Text size="sm" c="dimmed">
              Legal and merchant records used for claims and Stripe connected accounts
            </Text>
          </div>
          {canWrite && (
            <Button leftSection={<IconPlus size={16} />} onClick={openAddModal}>
              Add billing entity
            </Button>
          )}
        </Group>

        {listReady && (
          <BillingEntityDirectoryTable
            key={listKey}
            canWrite={canWrite}
            onRowClick={openEditModal}
          />
        )}
      </Stack>

      {canWrite && (
        <AddBillingEntityModal
          key={modalKey}
          opened={modalOpened}
          onClose={close}
          onSubmit={handleSave}
          initialValues={
            selected
              ? {
                  name: selected.name,
                  state: selected.state ?? '',
                  npi: selected.npi ?? '',
                  ein: selected.ein ?? '',
                  phone: getOrganizationPhone(selected.organization) ?? '',
                  street: getOrganizationUspsAddress(selected.organization).line ?? '',
                  city: getOrganizationUspsAddress(selected.organization).city ?? '',
                  postalCode: getOrganizationUspsAddress(selected.organization).postalCode ?? '',
                  stripeConnectedAccountId: selected.stripeAccount ?? '',
                }
              : undefined
          }
          editing={Boolean(selected)}
        />
      )}
    </Container>
  );
}
